import { RequesterError } from "web/script/modules/errors";
import requester from "web/script/modules/requester";
import logging from "web/script/utils/logging";
import { buildLanguageAwareUrlPath } from "web/script/utils/url";
import { CheckoutErrorResponse } from "web/types/pydantic-serializers";

/**
 * Checkout ownership cookie
 *
 * The backend sets a short-lived signed cookie at "Buy Now" and validates it on every
 * public checkout API call. When it is missing/expired the endpoints respond with a
 * `CheckoutOwnershipExpired` error. Rather than dumping the shopper on the homepage we
 * keep their product context — stashed here at initiation — and let them start a fresh
 * checkout for the same item in place.
 */
export const CHECKOUT_OWNERSHIP_EXPIRED: CheckoutErrorResponse["type"] = "CheckoutOwnershipExpired";

export const CHECKOUT_RESUME_STORAGE_KEY = "checkout.v2.resume";

// Must match the backend `settings.CHECKOUT_OWNERSHIP_MAX_AGE` (30 min). Used to proactively
// notify the shopper the moment the ownership cookie would be rejected server-side.
export const CHECKOUT_OWNERSHIP_MAX_AGE_MS = 30 * 60 * 1000;

const V2_CHECKOUT_SESSION_ENDPOINT = "/rest_api/checkout/v2/session/";

export interface CheckoutResumeItem {
    buy_option_id: string;
    size: string | null;
}

interface CheckoutResumePayload {
    items: CheckoutResumeItem[];
    purchaseIntentSessionId: string | null;
    // Epoch ms when the checkout (and its ownership cookie) was started.
    createdAt: number;
}

/** Persist the items being checked out so the flow can be restarted after the cookie expires. */
export function stashCheckoutResumeItems(
    items: CheckoutResumeItem[],
    purchaseIntentSessionId: string | null
): void {
    try {
        const payload: CheckoutResumePayload = {
            items,
            purchaseIntentSessionId,
            createdAt: Date.now(),
        };
        window.sessionStorage.setItem(CHECKOUT_RESUME_STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
        // sessionStorage can be unavailable (private mode / quota) — resume just won't be
        // possible. Log it so we can see how often it happens.
        logging.reportError(error as Error);
    }
}

/**
 * Epoch ms at which the current checkout's ownership cookie is considered expired, or null when
 * there is no active checkout stashed. Lets the UI schedule the "session expired" prompt to fire
 * exactly when the server would start rejecting the cookie.
 */
export function getCheckoutExpiryDeadline(): number | null {
    const payload = readCheckoutResumeItems();
    if (!payload || typeof payload.createdAt !== "number") {
        return null;
    }
    return payload.createdAt + CHECKOUT_OWNERSHIP_MAX_AGE_MS;
}

function readCheckoutResumeItems(): CheckoutResumePayload | null {
    try {
        const raw = window.sessionStorage.getItem(CHECKOUT_RESUME_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as CheckoutResumePayload;
        if (!parsed?.items?.length) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

/** True when the response body is the backend's ownership-expired error. */
export function isCheckoutOwnershipExpired(body: unknown): body is CheckoutErrorResponse {
    return (
        typeof body === "object" &&
        body !== null &&
        (body as CheckoutErrorResponse).type === CHECKOUT_OWNERSHIP_EXPIRED
    );
}

/** True when a failed request was rejected because the ownership cookie expired. */
export async function isOwnershipExpiredError(error: unknown): Promise<boolean> {
    if (!(error instanceof RequesterError)) {
        return false;
    }
    const body = await error.response.json().catch(() => null);
    return isCheckoutOwnershipExpired(body);
}

/**
 * Re-initiate checkout for the stashed item(s): mints a fresh ownership cookie and a new checkout
 * order, and restarts the expiry clock. Returns whether it succeeded so the caller can restart the
 * flow in place (no full-page reload). Returns false when there is nothing stashed to resume with.
 */
export async function resumeCheckout(): Promise<boolean> {
    const payload = readCheckoutResumeItems();

    if (!payload) {
        return false;
    }

    try {
        const endpoint = buildLanguageAwareUrlPath(V2_CHECKOUT_SESSION_ENDPOINT);
        await requester(endpoint, {
            method: "POST",
            body: JSON.stringify({
                purchase_intent_session_id: payload.purchaseIntentSessionId,
                items: payload.items,
            }),
        } as any);
        // A fresh cookie was just minted — restart the expiry clock for the resumed flow.
        stashCheckoutResumeItems(payload.items, payload.purchaseIntentSessionId);
        return true;
    } catch (error) {
        logging.reportError(error as Error);
        return false;
    }
}

/**
 * Abandon the expired checkout: drop the stashed context so a stale timer can't re-fire, then
 * send the shopper to the homepage.
 */
export function cancelCheckout(): void {
    try {
        window.sessionStorage.removeItem(CHECKOUT_RESUME_STORAGE_KEY);
    } catch {
        // sessionStorage can be unavailable — nothing to clear, still redirect.
    }
    window.location.assign(buildLanguageAwareUrlPath("/"));
}
